import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Lock, Unlock, CalendarRange, FileType2 } from "lucide-react";

export default function OrganizationPage() {
  const { token, isAdmin } = useAuth();
  const [branches, setBranches] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [series, setSeries] = useState([]);
  const [branchModal, setBranchModal] = useState(false);
  const [fyModal, setFyModal] = useState(false);
  const [seriesModal, setSeriesModal] = useState(false);
  const [branchForm, setBranchForm] = useState({ name: "", address: "", phone: "", is_main: false });
  const [fyForm, setFyForm] = useState({ name: "", start_date: "", end_date: "" });
  const [seriesForm, setSeriesForm] = useState({ code: "", doc_type: "FT", description: "" });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [b, f, s] = await Promise.all([
        axios.get(`${API_URL}/branches`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/fiscal-years`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/document-series`, { headers, withCredentials: true }),
      ]);
      setBranches(b.data); setFiscalYears(f.data); setSeries(s.data);
    } catch { toast.error("Erro ao carregar organização"); }
  };

  const createBranch = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/branches`, branchForm, { headers, withCredentials: true });
      toast.success("Filial criada");
      setBranchModal(false); setBranchForm({ name: "", address: "", phone: "", is_main: false });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const deleteBranch = async (b) => {
    if (!window.confirm(`Remover filial "${b.name}"?`)) return;
    await axios.delete(`${API_URL}/branches/${b.branch_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  const createFy = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/fiscal-years`, fyForm, { headers, withCredentials: true });
      toast.success("Exercício fiscal criado");
      setFyModal(false); setFyForm({ name: "", start_date: "", end_date: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const closeFy = async (fy) => {
    if (!window.confirm(`Fechar o exercício "${fy.name}"?`)) return;
    try {
      await axios.put(`${API_URL}/fiscal-years/${fy.fiscal_year_id}/close`, {}, { headers, withCredentials: true });
      toast.success("Exercício fechado");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const reopenFy = async (fy) => {
    try {
      await axios.put(`${API_URL}/fiscal-years/${fy.fiscal_year_id}/reopen`, {}, { headers, withCredentials: true });
      toast.success("Exercício reaberto");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const createSeries = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/document-series`, seriesForm, { headers, withCredentials: true });
      toast.success("Série criada");
      setSeriesModal(false); setSeriesForm({ code: "", doc_type: "FT", description: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const deleteSeries = async (s) => {
    if (!window.confirm(`Remover série "${s.code}"?`)) return;
    await axios.delete(`${API_URL}/document-series/${s.series_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  return (
    <div className="space-y-6" data-testid="organization-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit']">Organização</h1>
        <p className="text-muted-foreground">Filiais, exercícios fiscais e séries de documentos</p>
      </div>

      <Tabs defaultValue="branches">
        <TabsList>
          <TabsTrigger value="branches" data-testid="org-tab-branches">Filiais</TabsTrigger>
          <TabsTrigger value="fiscal" data-testid="org-tab-fiscal">Exercícios Fiscais</TabsTrigger>
          <TabsTrigger value="series" data-testid="org-tab-series">Séries de Documentos</TabsTrigger>
        </TabsList>

        <TabsContent value="branches" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setBranchModal(true)} data-testid="add-branch-btn"><Plus className="w-4 h-4 mr-2" /> Nova Filial</Button>
          </div>
          {branches.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground"><Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhuma filial registada</CardContent></Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {branches.map((b) => (
                <Card key={b.branch_id} data-testid={`branch-${b.branch_id}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold flex items-center gap-2">{b.name} {b.is_main && <Badge className="bg-primary/20 text-primary">Sede</Badge>}</div>
                        {b.address && <p className="text-xs text-muted-foreground">{b.address}</p>}
                        {b.phone && <p className="text-xs text-muted-foreground">{b.phone}</p>}
                      </div>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteBranch(b)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="fiscal" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setFyModal(true)} data-testid="add-fiscal-year-btn"><Plus className="w-4 h-4 mr-2" /> Novo Exercício</Button>
          </div>
          <div className="space-y-2">
            {fiscalYears.map((fy) => (
              <Card key={fy.fiscal_year_id} data-testid={`fiscal-year-${fy.fiscal_year_id}`}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CalendarRange className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-semibold">{fy.name}</p>
                      <p className="text-xs text-muted-foreground">{fy.start_date} → {fy.end_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={fy.status === "open" ? "bg-emerald-500/20 text-emerald-600" : "bg-red-500/20 text-red-500"}>
                      {fy.status === "open" ? "Aberto" : "Fechado"}
                    </Badge>
                    {fy.status === "open" ? (
                      <Button size="sm" variant="outline" onClick={() => closeFy(fy)} data-testid={`close-fy-${fy.fiscal_year_id}`}><Lock className="w-4 h-4 mr-1" /> Fechar</Button>
                    ) : isAdmin && (
                      <Button size="sm" variant="outline" onClick={() => reopenFy(fy)}><Unlock className="w-4 h-4 mr-1" /> Reabrir</Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {fiscalYears.length === 0 && <Card><CardContent className="py-10 text-center text-muted-foreground">Nenhum exercício fiscal definido</CardContent></Card>}
          </div>
        </TabsContent>

        <TabsContent value="series" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setSeriesModal(true)} data-testid="add-series-btn"><Plus className="w-4 h-4 mr-2" /> Nova Série</Button>
          </div>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {series.map((s) => (
              <Card key={s.series_id} data-testid={`series-${s.series_id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileType2 className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-mono font-bold">{s.code}</p>
                      <p className="text-xs text-muted-foreground">{s.doc_type}{s.description ? ` · ${s.description}` : ""}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteSeries(s)}><Trash2 className="w-4 h-4" /></Button>
                </CardContent>
              </Card>
            ))}
          </div>
          {series.length === 0 && <Card><CardContent className="py-10 text-center text-muted-foreground">Séries padrão em uso: FT, FR, NC, ORC, PC, GT, OF, OM, REQ</CardContent></Card>}
        </TabsContent>
      </Tabs>

      <Dialog open={branchModal} onOpenChange={setBranchModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nova Filial</DialogTitle></DialogHeader>
          <form onSubmit={createBranch} className="space-y-3">
            <div className="space-y-1"><Label>Nome *</Label><Input required value={branchForm.name} onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })} data-testid="branch-name-input" /></div>
            <div className="space-y-1"><Label>Morada</Label><Input value={branchForm.address} onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })} /></div>
            <div className="space-y-1"><Label>Telefone</Label><Input value={branchForm.phone} onChange={(e) => setBranchForm({ ...branchForm, phone: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBranchModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-branch-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={fyModal} onOpenChange={setFyModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Exercício Fiscal</DialogTitle></DialogHeader>
          <form onSubmit={createFy} className="space-y-3">
            <div className="space-y-1"><Label>Nome *</Label><Input required value={fyForm.name} onChange={(e) => setFyForm({ ...fyForm, name: e.target.value })} placeholder="Ex: Exercício 2026" data-testid="fy-name-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>Início *</Label><Input required type="date" value={fyForm.start_date} onChange={(e) => setFyForm({ ...fyForm, start_date: e.target.value })} /></div>
              <div className="space-y-1"><Label>Fim *</Label><Input required type="date" value={fyForm.end_date} onChange={(e) => setFyForm({ ...fyForm, end_date: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFyModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-fy-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={seriesModal} onOpenChange={setSeriesModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nova Série de Documento</DialogTitle></DialogHeader>
          <form onSubmit={createSeries} className="space-y-3">
            <div className="space-y-1"><Label>Código *</Label><Input required value={seriesForm.code} onChange={(e) => setSeriesForm({ ...seriesForm, code: e.target.value.toUpperCase() })} placeholder="Ex: FT-B" data-testid="series-code-input" /></div>
            <div className="space-y-1">
              <Label>Tipo de Documento</Label>
              <Select value={seriesForm.doc_type} onValueChange={(v) => setSeriesForm({ ...seriesForm, doc_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FT">Fatura (FT)</SelectItem>
                  <SelectItem value="FR">Fatura-Recibo (FR)</SelectItem>
                  <SelectItem value="NC">Nota de Crédito (NC)</SelectItem>
                  <SelectItem value="ORC">Orçamento (ORC)</SelectItem>
                  <SelectItem value="GT">Guia de Transporte (GT)</SelectItem>
                  <SelectItem value="PC">Ordem de Compra (PC)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Descrição</Label><Input value={seriesForm.description} onChange={(e) => setSeriesForm({ ...seriesForm, description: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSeriesModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-series-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
